import EmployeeSearchPicker from '../components/EmployeeSearchPicker';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useListRequest } from '../hooks/useListRequest';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Alert,
  Spinner,
  Button,
  Form,
  Modal,
  Table,
  Badge,
  Pagination
} from "react-bootstrap";
import Layout from "../components/Layout";
import { useAuth } from "../hooks/useAuth";
import { useRolePermissions } from "../hooks/useRolePermissions";
import { vacationAPI, isRequestCanceled } from "../utils/api";
import type { 
  VacationWithEmployee, 
  CreateVacationRequest,
  UpdateVacationRequest,
  CompanyVacationFilters 
} from "../types/vacation";
import type { EmployeeOption } from "../types/employee";
import { formatDateInCompanyTimezone, formatDateOnly, getTodayDateInCompanyTimezone, toDateOnly } from "../utils/timezone";
import { useTranslation } from "react-i18next";
import ScopeIndicator from '../components/ScopeIndicator';




const Vacations: React.FC = () => {
  const { currentCompany, companiesLoading, userRolePermissionsLoading } = useAuth();
  const { canView, canAdd, canEdit, canDelete } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'vacations' });
  const { t: tc } = useTranslation('common');
  
  // Get company timezone with fallback
  const companyTimezone = currentCompany?.timezone || 'UTC';
  
  // State management
  const [vacations, setVacations] = useState<VacationWithEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 50,
    total: 0
  });
  
  // Pagination states (matching employees page)
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Filter states
  const [filters, setFilters] = useState<CompanyVacationFilters>({
    from_date: getTodayDateInCompanyTimezone(companyTimezone),
    to_date: getTodayDateInCompanyTimezone(companyTimezone),
    per_page: 50,
    page: 1
  });
  
  const { begin, cancel } = useListRequest(JSON.stringify([currentCompany?.company_id, filters, rowsPerPage, page]));

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeOption | null>(null);
  const [editingVacation, setEditingVacation] = useState<VacationWithEmployee | null>(null);
  const [deletingVacation, setDeletingVacation] = useState<VacationWithEmployee | null>(null);
  const [newVacation, setNewVacation] = useState<CreateVacationRequest>({
    from_date: '',
    to_date: '',
    is_paid: true
  });
  const [editVacation, setEditVacation] = useState<UpdateVacationRequest>({
    from_date: '',
    to_date: '',
    is_paid: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  // Initialize dates when company timezone changes
  useEffect(() => {
    if (currentCompany) {
      const today = getTodayDateInCompanyTimezone(currentCompany.timezone || 'UTC');
      setFilters({
        from_date: today,
        to_date: today,
        per_page: 50,
        page: 1
      });
      setPage(1);
    }
  }, [currentCompany]);

  // Load vacations data
  const loadVacations = useCallback(async () => {
    if (!currentCompany?.company_id) return;
    const { signal } = begin();
    if (signal.aborted) return;

    setLoading(true);
    setError(null);
    setVacations([]);
    setPagination({ current_page: page, per_page: rowsPerPage, total: 0 });

    try {
      const response = await vacationAPI.getCompanyVacations(currentCompany.company_id, {
        ...filters,
        include_employee: 1, // Always include employee data
        per_page: rowsPerPage,
        page: page
      }, signal);
      if (signal.aborted) return;
      setVacations(response.data.items);
      
      // Calculate total from last_page and per_page
      // If we have a total from API, use it; otherwise calculate from pagination data
      let actualTotal = response.data.total;
      
      if (!actualTotal) {
        // Calculate total from last_page and per_page
        const estimatedTotal = response.data.last_page * response.data.per_page;
        
        // If we're on the last page, calculate actual total from items
        if (response.data.current_page === response.data.last_page) {
          actualTotal = (response.data.current_page - 1) * response.data.per_page + response.data.items.length;
        } else {
          actualTotal = estimatedTotal;
        }
      }
      
      setPagination({
        current_page: response.data.current_page,
        per_page: response.data.per_page,
        total: actualTotal
      });
    } catch (err: any) {
      if (signal.aborted || isRequestCanceled(err)) return;
      console.error('Failed to load vacations:', err);
      setError(err.response?.data?.message || t('fetchFailed'));
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [currentCompany?.company_id, filters, rowsPerPage, page, begin, t]);
  const latestLoadVacations = useRef(loadVacations);
  latestLoadVacations.current = loadVacations;

  // Load data when component mounts or filters change

  useEffect(() => {
    loadVacations();
    return cancel;
  }, [loadVacations, cancel]);

  // Handle filter changes
  const handleFilterChange = (field: keyof CompanyVacationFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  // Pagination handlers (matching employees page)
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to first page when changing items per page
  }, []);

  // Calculate pagination
  const totalPages = Math.ceil(pagination.total / rowsPerPage);

  // Generate pagination items (matching employees page)
  const renderPaginationItems = useCallback(() => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages, handlePageChange]);

  // Handle add vacation modal
  const handleShowAddModal = () => {
    setSelectedEmployee(null);
    setNewVacation({
      from_date: getTodayDateInCompanyTimezone(companyTimezone),
      to_date: getTodayDateInCompanyTimezone(companyTimezone),
      is_paid: true
    });
    setModalError(null);
    setModalSuccess(null);
    setError(null); // Clear any main page errors
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setSelectedEmployee(null);
    setNewVacation({
      from_date: '',
      to_date: '',
      is_paid: true
    });
    setModalError(null);
    setModalSuccess(null);
  };

  // Handle vacation submission
  const handleSubmitVacation = async () => {
    if (!selectedEmployee) {
      setModalError(t('selectEmployeeError'));
      return;
    }

    setSubmitting(true);
    setModalError(null);
    setModalSuccess(null);
    
    try {
      const response = await vacationAPI.createEmployeeVacation(selectedEmployee.id, newVacation);
      setModalSuccess(response.message || t('createSuccess'));
      
      // Reset to first page and refresh the list
      setPage(1);
      // Small delay to ensure cache is cleared
      setTimeout(async () => {
        await latestLoadVacations.current();
      }, 100);
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseAddModal();
      }, 2000);
      
    } catch (err: any) {
      console.error('Failed to create vacation:', err);
      setModalError(err.response?.data?.message || t('saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit vacation
  const handleEditVacation = (vacation: VacationWithEmployee) => {
    setEditingVacation(vacation);
    setEditVacation({
      from_date: vacation.from_date,
      to_date: vacation.to_date,
      is_paid: vacation.is_paid,
    });
    setShowEditModal(true);
    setModalError(null);
    setModalSuccess(null);
  };

  // Handle delete vacation
  const handleDeleteVacation = (vacation: VacationWithEmployee) => {
    setDeletingVacation(vacation);
    setShowDeleteModal(true);
  };

  // Handle edit vacation submission
  const handleEditSubmit = async () => {
    if (!editingVacation) return;

    setSubmitting(true);
    setModalError(null);

    try {
      await vacationAPI.updateEmployeeVacation(
        editingVacation.employee_id,
        editingVacation.id,
        editVacation
      );

      setModalSuccess(t('updateSuccess'));
      await latestLoadVacations.current();
      
      setTimeout(() => {
        setShowEditModal(false);
        setEditingVacation(null);
        setModalSuccess(null);
      }, 1500);
    } catch (error: any) {
      console.error('Error updating vacation:', error);
      setModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete vacation confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingVacation) return;

    setSubmitting(true);
    setModalError(null);

    try {
      await vacationAPI.deleteEmployeeVacation(
        deletingVacation.employee_id,
        deletingVacation.id
      );

      setModalSuccess(t('deleteSuccess'));
      await latestLoadVacations.current();
      
      setTimeout(() => {
        setShowDeleteModal(false);
        setDeletingVacation(null);
        setModalSuccess(null);
      }, 1500);
    } catch (error: any) {
      console.error('Error deleting vacation:', error);
      setModalError(error.response?.data?.message || t('deleteFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle close edit modal
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingVacation(null);
    setEditVacation({ from_date: '', to_date: '', is_paid: true });
    setModalError(null);
    setModalSuccess(null);
  };

  // Handle close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingVacation(null);
    setModalError(null);
    setModalSuccess(null);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!currentCompany?.timezone) return tc('messages.na');
    return formatDateOnly(dateString);
  };

  const formatCreatedAt = (dateString: string) => {
    if (!currentCompany?.timezone) return tc('messages.na');
    return formatDateInCompanyTimezone(dateString, currentCompany.timezone);
  };

  // Calculate vacation duration
  const calculateDuration = (fromDate: string, toDate: string) => {
    const toCalendarDay = (value: string): number | null => {
      const match = toDateOnly(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!match) return null;

      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const timestamp = Date.UTC(year, month - 1, day);
      const parsed = new Date(timestamp);
      if (
        parsed.getUTCFullYear() !== year ||
        parsed.getUTCMonth() !== month - 1 ||
        parsed.getUTCDate() !== day
      ) {
        return null;
      }

      return Math.floor(timestamp / 86_400_000);
    };

    const fromDay = toCalendarDay(fromDate);
    const toDay = toCalendarDay(toDate);
    if (fromDay === null || toDay === null) return 0;
    return Math.abs(toDay - fromDay) + 1;
  };

  // Check if vacation is active today
  const isActiveToday = (fromDate: string, toDate: string) => {
    const today = getTodayDateInCompanyTimezone(companyTimezone);
    return today >= fromDate && today <= toDate;
  };

  if (companiesLoading || userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container className="d-flex justify-content-center align-items-center an-enterprise-page an-page-shell an-data-page" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
            <p className="text-muted">{t('loading')}</p>
          </div>
        </Container>
      </Layout>
    );
  }

  // Check if user has permission to view vacations
  if (!canView('employeesVacation')) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page">
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>{t('accessDeniedTitle')}</Alert.Heading>
            <p className="mb-0">{t('accessDeniedBody')}</p>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page">
        <div className="page-header">
          <h2 className="an-page-title">
            {t('title')}
          </h2>
          {currentCompany && canAdd('employeesVacation') && (
            <Button variant="primary" onClick={handleShowAddModal}>
              <i className="bi bi-plus-lg"></i>
              {t('addVacation')}
            </Button>
          )}
        </div>

        {currentCompany && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentCompany.description} (ID: {currentCompany.company_id})
          </Alert>
        )}

        <ScopeIndicator resource="employeesVacation" />

        {!currentCompany && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectCompany', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
            <Alert.Heading>{t('errorTitle')}</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {currentCompany && (
          <Card className="mb-4 an-data-card">
            <Card.Body className="an-data-card">
              <Form onSubmit={(e) => { e.preventDefault(); }}>
                <Row>
                  <Col lg={3} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{tc('labels.fromDate')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.from_date}
                        onChange={(e) => handleFilterChange('from_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={3} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{tc('labels.toDate')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.to_date}
                        onChange={(e) => handleFilterChange('to_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={3} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{tc('labels.employee')}</Form.Label>
                      <EmployeeSearchPicker companyId={currentCompany?.company_id} value={filters.employee_id || ''}
                        onChange={employee => handleFilterChange('employee_id', employee?.id)}
                        placeholder={tc('labels.allEmployees')} />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Button type="button" variant="outline-secondary" onClick={() => {
                        setFilters({
                          from_date: getTodayDateInCompanyTimezone(companyTimezone),
                          to_date: getTodayDateInCompanyTimezone(companyTimezone),
                          per_page: 50,
                          page: 1
                        });
                      }}>
                        {tc('buttons.clearFilters')}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>{t('loadingData')}</span>
          </div>
        )}

        {currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="p-0 an-data-card">
              {vacations.length === 0 ? (
                <div className="text-center py-4 py-md-5">
                  <div style={{ fontSize: '3rem', opacity: '0.3', marginBottom: '1rem' }}>🏖️</div>
                  <h5 className="an-empty-title">{t('noTitle')}</h5>
                  <p className="an-empty-text mb-0">
                    {t('noRecords')}
                  </p>
                </div>
              ) : (
                <>
                  <div className="d-none d-md-block">
                    <div className="table-responsive an-data-table an-panel">
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{tc('labels.employee')}</th>
                            <th>{tc('labels.fromDate')}</th>
                            <th>{tc('labels.toDate')}</th>
                            <th>{t('duration')}</th>
                            <th>{tc('labels.status')}</th>
                            <th>{tc('labels.created')}</th>
                            <th>{tc('labels.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vacations.map((vacation) => (
                            <tr key={vacation.id}>
                              <td>
                                <div>
                                  <div className="an-body-text">{vacation.employee?.name || t('unknown')}</div>
                                  <small className="text-muted">{t('employeeIdLabel', { id: vacation.employee_id })}</small>
                                </div>
                              </td>
                              <td>
                                <Badge bg="light" text="dark">
                                  {formatDate(vacation.from_date)}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg="light" text="dark">
                                  {formatDate(vacation.to_date)}
                                </Badge>
                              </td>
                              <td>
                                <span className="fw-medium text-primary">
                                  {t('days', { count: calculateDuration(vacation.from_date, vacation.to_date) })}
                                </span>
                              </td>
                              <td>
                                {isActiveToday(vacation.from_date, vacation.to_date) ? (
                                  <Badge bg="success">
                                    <i className="bi bi-check-circle me-1"></i>
                                    {tc('status.active')}
                                  </Badge>
                                ) : vacation.from_date > getTodayDateInCompanyTimezone(companyTimezone) ? (
                                  <Badge bg="info">
                                    <i className="bi bi-clock me-1"></i>
                                    {t('upcoming')}
                                  </Badge>
                                ) : (
                                  <Badge bg="secondary">
                                    <i className="bi bi-calendar-check me-1"></i>
                                    {t('completed')}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <small className="text-muted">
                                  {formatCreatedAt(vacation.created_at)}
                                </small>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  {canEdit('employeesVacation') && (
                                    <Button variant="link" size="sm" className="text-primary p-1 me-3 text-decoration-none" onClick={() => handleEditVacation(vacation)} title={t('editTooltip')}><i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span></Button>
                                  )}
                                  {canDelete('employeesVacation') && (
                                    <Button variant="link" size="sm" className="text-danger p-1 text-decoration-none" onClick={() => handleDeleteVacation(vacation)} title={t('deleteTooltip')}><i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span></Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  <div className="d-md-none">
                    {vacations.map((vacation) => (
                      <Card key={vacation.id} className="mb-3 border-0 shadow-sm an-data-card">
                        <Card.Body className="p-3 an-data-card">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold">{vacation.employee?.name || t('unknown')}</h6>
                              <small className="text-muted">{t('employeeIdLabel', { id: vacation.employee_id })}</small>
                            </div>
                            {isActiveToday(vacation.from_date, vacation.to_date) ? (
                              <Badge bg="success" className="px-2 py-1">
                                <i className="bi bi-check-circle me-1"></i>
                                {tc('status.active')}
                              </Badge>
                            ) : vacation.from_date > getTodayDateInCompanyTimezone(companyTimezone) ? (
                              <Badge bg="info" className="px-2 py-1">
                                <i className="bi bi-clock me-1"></i>
                                {t('upcoming')}
                              </Badge>
                            ) : (
                              <Badge bg="secondary" className="px-2 py-1">
                                <i className="bi bi-calendar-check me-1"></i>
                                {t('completed')}
                              </Badge>
                            )}
                          </div>
                          <Row className="g-2">
                            <Col xs={6}>
                              <small className="text-muted d-block">{tc('labels.fromDate')}</small>
                              <Badge bg="light" text="dark" className="px-2 py-1">
                                {formatDate(vacation.from_date)}
                              </Badge>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted d-block">{tc('labels.toDate')}</small>
                              <Badge bg="light" text="dark" className="px-2 py-1">
                                {formatDate(vacation.to_date)}
                              </Badge>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted d-block">{t('duration')}</small>
                              <span className="fw-medium text-primary">
                                {t('days', { count: calculateDuration(vacation.from_date, vacation.to_date) })}
                              </span>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted d-block">{tc('labels.created')}</small>
                              <small className="text-muted">
                                {formatCreatedAt(vacation.created_at)}
                              </small>
                            </Col>
                          </Row>
                          {(canEdit('employeesVacation') || canDelete('employeesVacation')) && (
                            <div className="d-flex gap-2 mt-3 pt-2 border-top">
                              {canEdit('employeesVacation') && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditVacation(vacation)}
                                  className="d-flex align-items-center"
                                >
                                  <i className="bi bi-pencil"></i>
                                  <span className="ms-1">{tc('buttons.edit')}</span>
                                </Button>
                              )}
                              {canDelete('employeesVacation') && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteVacation(vacation)}
                                  className="d-flex align-items-center"
                                >
                                  <i className="bi bi-trash"></i>
                                  <span className="ms-1">{tc('buttons.delete')}</span>
                                </Button>
                              )}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </Card.Body>

            {pagination.total > rowsPerPage && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <span className="text-muted small">
                  {tc('messages.showing', { from: ((page - 1) * rowsPerPage) + 1, to: Math.min(page * rowsPerPage, pagination.total), total: pagination.total, entity: t('title').toLowerCase() })}
                </span>
                <Pagination className="mb-0 an-pagination">
                  <Pagination.Prev
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                  />
                  {renderPaginationItems()}
                  <Pagination.Next
                    disabled={page === totalPages}
                    onClick={() => handlePageChange(page + 1)}
                  />
                </Pagination>
              </div>
            )}
          </Card>
        )}

        {!currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="text-center py-5 an-data-card">
              <h5>{tc('messages.selectCompanyTitle')}</h5>
              <p className="text-muted">
                {tc('messages.selectCompanyBody', { entity: t('title').toLowerCase() })}
              </p>
            </Card.Body>
          </Card>
        )}

        {currentCompany && !loading && (
          <Row className="mt-3 mb-4">
            <Col className="d-flex justify-content-center align-items-center gap-2">
              <span className="text-muted small">{tc('messages.itemsPerPage')}</span>
              <Form.Select
                size="sm"
                style={{ width: 'auto' }}
                value={rowsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </Col>
          </Row>
        )}
      </Container>

      {/* Add Vacation Modal */}
        <Modal show={showAddModal} onHide={handleCloseAddModal} size="lg" centered>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={(e) => { e.preventDefault(); handleSubmitVacation(); }}>
            <Modal.Body className="py-2 py-md-3">
            {modalSuccess && (
              <Alert variant="success" className="mb-3">
                <i className="bi bi-check-circle me-2"></i>
                {modalSuccess}
              </Alert>
            )}
            
            {modalError && (
              <Alert variant="danger" className="mb-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {modalError}
              </Alert>
            )}
            
              <Row className="g-2 g-md-3">
                <Col xs={12} className="mb-2 mb-md-3">
                  <Form.Group>
                    <Form.Label className="small fw-medium">{t('selectEmployee')}</Form.Label>
                    <EmployeeSearchPicker companyId={currentCompany?.company_id} value={selectedEmployee?.id || ''}
                      onChange={setSelectedEmployee} required size="sm" placeholder={t('chooseEmployee')} />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-2 g-md-3">
                <Col xs={12} sm={6} className="mb-2 mb-md-3">
                  <Form.Group>
                    <Form.Label className="small fw-medium">{tc('labels.fromDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={newVacation.from_date}
                      onChange={(e) => setNewVacation(prev => ({ ...prev, from_date: e.target.value }))}
                      required
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} className="mb-2 mb-md-3">
                  <Form.Group>
                    <Form.Label className="small fw-medium">{tc('labels.toDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={newVacation.to_date}
                      onChange={(e) => setNewVacation(prev => ({ ...prev, to_date: e.target.value }))}
                      required
                      size="sm"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-2 g-md-3">
                <Col xs={12} className="mb-2">
                  <Form.Check type="switch" id="newVacationIsPaid" label={t('paid', { defaultValue: 'Paid' })} checked={newVacation.is_paid ?? true} onChange={e => setNewVacation(prev => ({ ...prev, is_paid: e.target.checked }))} />
                </Col>
              </Row>

              {selectedEmployee && (
                <Row className="g-2 g-md-3">
                  <Col xs={12} className="mb-2 mb-md-3">
                    <Alert variant="info" className="py-2 py-md-3">
                      <div className="d-flex align-items-center">
                        <div style={{
                          width: '35px',
                          height: '35px',
                          borderRadius: '50%',
                          background: 'var(--an-primary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '0.75rem',
                          color: 'var(--an-white)',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          flexShrink: 0
                        }}>
                          {selectedEmployee.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-bold small">{selectedEmployee.name}</div>
                          <small className="text-muted">{t('employeeIdLabel', { id: selectedEmployee.emp_id })}</small>
                        </div>
                      </div>
                    </Alert>
                  </Col>
                </Row>
              )}

              {newVacation.from_date && newVacation.to_date && (
                <Row className="g-2 g-md-3">
                  <Col xs={12} className="mb-2 mb-md-3">
                    <Alert variant="success" className="py-2 py-md-3">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-calendar-check me-2" style={{ fontSize: '1rem', flexShrink: 0 }}></i>
                        <div className="flex-grow-1">
                          <div className="fw-bold small">{t('vacationDuration')}</div>
                          <div className="text-success small">
                            {t('days', { count: calculateDuration(newVacation.from_date, newVacation.to_date) })}
                            {newVacation.from_date === newVacation.to_date && ` ${t('singleDay')}`}
                          </div>
                        </div>
                      </div>
                    </Alert>
                  </Col>
                </Row>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseAddModal} disabled={submitting}>
                {tc('buttons.cancel')}
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={submitting || !selectedEmployee || !newVacation.from_date || !newVacation.to_date}
              >
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t('adding')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-lg"></i>
                    {t('addVacation')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Edit Vacation Modal */}
        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg" centered>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('editTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={(e) => { e.preventDefault(); handleEditSubmit(); }}>
            <Modal.Body className="py-2 py-md-3">
              {modalError && (
                <Alert variant="danger" className="mb-3">
                  {modalError}
                </Alert>
              )}
              {modalSuccess && (
                <Alert variant="success" className="mb-3">
                  {modalSuccess}
                </Alert>
              )}
              
              {editingVacation && (
                <div className="mb-3">
                  <h6 className="text-muted mb-2">{tc('labels.employee')}</h6>
                  <div className="p-2 bg-light rounded">
                    <strong>{editingVacation.employee?.name || t('unknown')}</strong>
                    <br />
                    <small className="text-muted">{t('employeeIdLabel', { id: editingVacation.employee_id })}</small>
                  </div>
                </div>
              )}

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('fromDateRequired')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={editVacation.from_date}
                      onChange={(e) => setEditVacation({ ...editVacation, from_date: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('toDateRequired')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={editVacation.to_date}
                      onChange={(e) => setEditVacation({ ...editVacation, to_date: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} className="mb-2">
                  <Form.Check type="switch" id="editIsPaid" label={t('paid', { defaultValue: 'Paid' })} checked={editVacation.is_paid ?? true} onChange={e=> setEditVacation({ ...editVacation, is_paid: e.target.checked })} />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="py-2 py-md-3">
              <Button variant="secondary" onClick={handleCloseEditModal} disabled={submitting}>
                {tc('buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {tc('loading.updating')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-pencil"></i>
                    {t('updateVacation')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Vacation Modal */}
        <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('deleteTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-2 py-md-3">
            {modalError && (
              <Alert variant="danger" className="mb-3">
                {modalError}
              </Alert>
            )}
            {modalSuccess && (
              <Alert variant="success" className="mb-3">
                {modalSuccess}
              </Alert>
            )}
            
            {deletingVacation && (
              <div>
                <p className="mb-3">
                  {t('deleteConfirmFor', { name: deletingVacation.employee?.name || t('unknown') })}
                </p>
                <div className="p-3 bg-light rounded">
                  <Row>
                    <Col xs={6}>
                      <small className="text-muted d-block">{tc('labels.fromDate')}</small>
                      <strong>{formatDate(deletingVacation.from_date)}</strong>
                    </Col>
                    <Col xs={6}>
                      <small className="text-muted d-block">{tc('labels.toDate')}</small>
                      <strong>{formatDate(deletingVacation.to_date)}</strong>
                    </Col>
                    <Col xs={12} className="mt-2">
                      <small className="text-muted d-block">{t('duration')}</small>
                      <strong>{t('days', { count: calculateDuration(deletingVacation.from_date, deletingVacation.to_date) })}</strong>
                    </Col>
                  </Row>
                </div>
                <p className="text-danger mt-3 mb-0">
                  <small>{tc('messages.cannotBeUndone')}</small>
                </p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="py-2 py-md-3">
            <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={submitting}>
              {tc('buttons.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {tc('loading.deleting')}
                </>
              ) : (
                <>
                  <i className="bi bi-trash"></i>
                  {t('deleteVacation')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
    </Layout>
  );
};

export default Vacations;
